/**
 * Reusable inspector field primitives. Shared between the schema-driven
 * renderer and the bespoke per-primitive sub-panels (Extract, Aggregate,
 * Verify, etc.).
 */

import React from 'react';
import clsx from 'clsx';
import { Switch } from '@headlessui/react';
import { MapPinIcon } from '@heroicons/react/24/outline';

import { useBuilder } from '~/pages/workflows/builder/state/context';
import { BindingPicker } from './binding-picker';
import type { InputFieldDescriptor } from '~/pages/workflows/builder/types';

export function FieldRow(props: {
  label: string;
  description?: string;
  required?: boolean;
  pinnable?: boolean;
  pinned?: boolean;
  onTogglePin?: () => void;
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <div className="space-y-1" data-field-id={props.id}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-xs font-medium text-neutral-700 dark:text-neutral-300">
          <span>{props.label}</span>
          {props.required && <span className="text-rose-500 dark:text-rose-400">*</span>}
        </div>
        {props.pinnable && props.onTogglePin && (
          <button
            type="button"
            onClick={props.onTogglePin}
            title={props.pinned ? 'Unpin from Run form' : 'Pin to Run form'}
            className={clsx(
              'inline-flex items-center rounded p-0.5 text-[10px]',
              props.pinned
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-neutral-300 dark:text-neutral-600 hover:text-neutral-500 dark:hover:text-neutral-400'
            )}
          >
            <MapPinIcon className="h-3 w-3" />
          </button>
        )}
      </div>
      {props.description && (
        <p className="text-[11px] text-neutral-500 dark:text-neutral-400">{props.description}</p>
      )}
      <div>{props.children}</div>
    </div>
  );
}

export function TextInput(props: {
  value: string | undefined;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  invalid?: boolean;
}) {
  if (props.multiline) {
    return (
      <textarea
        rows={4}
        value={props.value ?? ''}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        className={clsx(
          'w-full rounded-md border py-1.5 px-2 text-sm',
          props.invalid
            ? 'border-rose-300 dark:border-rose-500/30'
            : 'border-neutral-200 dark:border-neutral-700',
          'focus:border-primary-300 dark:focus:border-primary-500/30 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/30'
        )}
      />
    );
  }
  return (
    <input
      type="text"
      value={props.value ?? ''}
      placeholder={props.placeholder}
      onChange={(e) => props.onChange(e.target.value)}
      className={clsx(
        'w-full rounded-md border py-1.5 px-2 text-sm',
        props.invalid
          ? 'border-rose-300 dark:border-rose-500/30'
          : 'border-neutral-200 dark:border-neutral-700',
        'focus:border-primary-300 dark:focus:border-primary-500/30 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/30'
      )}
    />
  );
}

export function NumberInput(props: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <input
      type="number"
      value={props.value ?? ''}
      onChange={(e) => props.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 py-1.5 px-2 text-sm focus:border-primary-300 dark:focus:border-primary-500/30 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/30"
    />
  );
}

export function BooleanInput(props: {
  value: boolean | undefined;
  onChange: (v: boolean) => void;
}) {
  return (
    <Switch
      checked={Boolean(props.value)}
      onChange={props.onChange}
      className={clsx(
        'relative inline-flex h-5 w-9 items-center rounded-full transition',
        props.value ? 'bg-primary-600' : 'bg-neutral-300 dark:bg-neutral-600'
      )}
    >
      <span
        className={clsx(
          'inline-block h-3.5 w-3.5 transform rounded-full bg-white dark:bg-neutral-800 transition',
          props.value ? 'translate-x-5' : 'translate-x-1'
        )}
      />
    </Switch>
  );
}

export function Select(props: {
  value: string | undefined;
  onChange: (v: string) => void;
  options: { value: string; label: string; description?: string }[];
  invalid?: boolean;
}) {
  return (
    <select
      value={props.value ?? ''}
      onChange={(e) => props.onChange(e.target.value)}
      className={clsx(
        'w-full rounded-md border py-1.5 px-2 text-sm',
        props.invalid
          ? 'border-rose-300 dark:border-rose-500/30'
          : 'border-neutral-200 dark:border-neutral-700',
        'focus:border-primary-300 dark:focus:border-primary-500/30 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/30'
      )}
    >
      <option value="" disabled>
        Select…
      </option>
      {props.options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export function ChipMultiSelect(props: {
  value: string[] | undefined;
  onChange: (v: string[]) => void;
  options?: { value: string; label: string }[];
  placeholder?: string;
}) {
  const [draft, setDraft] = React.useState('');
  const value = props.value ?? [];
  const add = (v: string) => {
    const trimmed = v.trim();
    if (!trimmed) return;
    if (value.includes(trimmed)) return;
    props.onChange([...value, trimmed]);
    setDraft('');
  };
  const remove = (v: string) => props.onChange(value.filter((x) => x !== v));
  return (
    <div className="rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-1">
        {value.map((v) => {
          const labelled = props.options?.find((o) => o.value === v)?.label ?? v;
          return (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full bg-primary-50 dark:bg-primary-500/10 px-2 py-0.5 text-xs text-primary-700 dark:text-primary-300"
            >
              {labelled}
              <button
                type="button"
                onClick={() => remove(v)}
                className="text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
              >
                ×
              </button>
            </span>
          );
        })}
        <input
          type="text"
          value={draft}
          placeholder={value.length === 0 ? (props.placeholder ?? 'Add…') : ''}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add(draft);
            }
            if (e.key === 'Backspace' && !draft && value.length > 0) {
              remove(value[value.length - 1]);
            }
          }}
          onBlur={() => draft && add(draft)}
          className="flex-1 min-w-[80px] border-0 bg-transparent text-sm focus:outline-none focus:ring-0"
        />
      </div>
      {props.options && (
        <div className="mt-1 flex flex-wrap gap-1">
          {props.options
            .filter((o) => !value.includes(o.value))
            .map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => add(o.value)}
                className="rounded-full border border-neutral-200 dark:border-neutral-700 px-2 py-0.5 text-[11px] text-neutral-600 dark:text-neutral-400 hover:border-primary-300 dark:hover:border-primary-500/30 hover:text-primary-700 dark:hover:text-primary-300"
              >
                + {o.label}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

/**
 * Renders the schema-driven form for a primitive. The caller controls which
 * fields are shown by passing `fields`; unrelated per-primitive extras
 * (EXTRACT entities, OUTPUT_INTEGRATION actions) are rendered by bespoke
 * subforms outside this.
 */
export function SchemaForm(props: {
  nodeId: string;
  fields: InputFieldDescriptor[];
  focusFieldKey?: string | null;
}) {
  const { state, dispatch } = useBuilder();
  const node = state.nodes.find((n) => n.id === props.nodeId);
  if (!node) return null;

  const visible = props.fields.filter((f) => {
    if (!f.when) return true;
    const value = node.config[f.when.field];
    return f.when.equals.includes(String(value ?? ''));
  });

  return (
    <div className="space-y-3">
      {visible.map((field) => {
        const value = node.config[field.key];
        const focusRing =
          props.focusFieldKey === field.key
            ? 'ring-2 ring-primary-300 dark:ring-primary-500/30 ring-offset-2 rounded'
            : '';
        return (
          <div key={field.key} className={focusRing}>
            <FieldRow
              id={field.key}
              label={field.label}
              description={field.description}
              required={field.required}
              pinnable={field.fieldKind === 'CONFIG'}
              pinned={node.pinnedFields?.includes(field.key)}
              onTogglePin={() =>
                dispatch({ type: 'toggle-pinned', nodeId: node.id, configKey: field.key })
              }
            >
              {field.fieldKind === 'INPUT' ||
              field.fieldKind === 'DATA' ||
              field.fieldKind === 'FILE' ? (
                <BindingPicker nodeId={props.nodeId} field={field} />
              ) : (
                <ConfigEditor
                  field={field}
                  value={value}
                  onChange={(v) =>
                    dispatch({
                      type: 'set-config',
                      nodeId: props.nodeId,
                      configKey: field.key,
                      value: v
                    })
                  }
                />
              )}
            </FieldRow>
          </div>
        );
      })}
    </div>
  );
}

function ConfigEditor(props: {
  field: InputFieldDescriptor;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const { field, value, onChange } = props;

  if (field.options && !field.isList) {
    return (
      <Select value={value as string | undefined} onChange={onChange} options={field.options} />
    );
  }
  if (field.chipInput || field.isList) {
    return (
      <ChipMultiSelect
        value={value as string[] | undefined}
        onChange={onChange}
        options={field.options}
        placeholder="Type and press Enter"
      />
    );
  }
  if (field.valueType === 'bool') {
    return <BooleanInput value={value as boolean | undefined} onChange={onChange} />;
  }
  if (field.valueType === 'number') {
    return <NumberInput value={value as number | undefined} onChange={(v) => onChange(v)} />;
  }
  if (field.valueType === 'json') {
    return <TextInput value={valueToString(value)} onChange={onChange} multiline />;
  }
  return <TextInput value={valueToString(value)} onChange={onChange} multiline={field.multiline} />;
}

function valueToString(v: unknown): string {
  if (v === undefined || v === null) return '';
  if (typeof v === 'string') return v;
  return JSON.stringify(v, null, 2);
}
